import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, LogOut, Menu } from "lucide-react";
import { getRouteMeta } from "./navConfig";
import HeaderSearch from "./HeaderSearch";
import { TaskCenterBell } from "./TaskCenterContext";
import WarehouseSwitcher from "./WarehouseSwitcher";
import demoProfile from "../../Assets/demoProfile.svg";

const TopNav = ({ onMenu }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { crumbs } = getRouteMeta(pathname);

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("loginObj")) || {};
  } catch (e) {
    user = {};
  }
  const fullName =
    [user.salutation, user.firstName, user.lastName].filter(Boolean).join(" ") ||
    "Pharmacy user";
  const shortName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || "Pharmacy user";
  const initials =
    [user.firstName, user.lastName]
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "CB";
  const photo = user.photoName
    ? (process.env.REACT_APP_API_GCP_URL || "") + user.photoName
    : demoProfile;

  const [showProfile, setShowProfile] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <header
      className="shrink-0 flex items-center gap-2 sm:gap-3.5 px-2 sm:px-4 bg-panel border-b border-hairline"
      style={{ height: "var(--topbar-h)" }}
    >
      {/* mobile menu */}
      <button
        onClick={onMenu}
        aria-label="Open menu"
        className="lg:hidden w-9 h-9 -ml-0.5 flex items-center justify-center rounded-cb-sm text-fg-default hover:bg-surface-hover shrink-0"
      >
        <Menu size={20} strokeWidth={1.75} />
      </button>

      {/* breadcrumbs — only the current page on small screens */}
      <nav className="flex items-center gap-1.5 text-t-md min-w-0">
        <span className="text-fg-muted shrink-0 hidden md:inline">CureBay</span>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            <ChevronRight size={13} strokeWidth={1.9} className="text-fg-subtle shrink-0 hidden md:inline" />
            <span
              className={
                i === crumbs.length - 1
                  ? "font-semibold text-fg-strong truncate"
                  : "text-fg-muted truncate hidden md:inline"
              }
            >
              {c}
            </span>
          </React.Fragment>
        ))}
      </nav>

      {/* global order search */}
      <HeaderSearch />

      <div className="flex-1" />

      {/* the active store — scopes every screen, so it sits next to the identity */}
      <WarehouseSwitcher />

      <span className="w-px h-6 bg-hairline hidden sm:block" />

      {/* notifications — async task center */}
      <TaskCenterBell />

      <span className="w-px h-6 bg-hairline hidden sm:block" />

      {/* profile */}
      <div className="relative" ref={ref}>
        <button
          onClick={() => setShowProfile((s) => !s)}
          className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-cb-sm hover:bg-surface-hover transition-colors"
        >
          <span className="w-7 h-7 rounded-full overflow-hidden bg-ink-600 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
            <img
              src={photo}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.textContent = initials;
              }}
            />
          </span>
          <span className="text-left leading-tight hidden sm:block">
            <span className="block text-t-sm font-semibold text-fg-strong">
              {shortName}
            </span>
            <span className="block text-[10px] text-fg-muted font-mono">Admin</span>
          </span>
        </button>

        {showProfile && (
          <div className="absolute top-[44px] right-0 w-64 max-w-[calc(100vw-16px)] bg-panel border border-hairline-subtle rounded-cb-md shadow-cb-md p-3 z-50 cb-animate-in">
            <div className="flex items-center gap-2.5 pb-3 border-b border-hairline-subtle">
              <span className="w-9 h-9 rounded-full overflow-hidden bg-ink-600 text-white flex items-center justify-center text-t-sm font-semibold shrink-0">
                <img
                  src={photo}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentNode.textContent = initials;
                  }}
                />
              </span>
              <div className="min-w-0">
                <div className="text-t-md font-semibold text-fg-strong truncate">
                  {fullName}
                </div>
                <div className="text-t-xs text-fg-muted">Admin</div>
              </div>
            </div>
            <div className="py-2.5 flex flex-col gap-2">
              <div>
                <div className="cb-eyebrow">Email</div>
                <div className="text-t-sm text-fg-default truncate mt-0.5">
                  {user.email || "—"}
                </div>
              </div>
              <div>
                <div className="cb-eyebrow">Mobile</div>
                <div className="text-t-sm text-fg-default font-mono mt-0.5">
                  {user.mobile || "—"}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-1 flex items-center justify-center gap-2 h-8 rounded-cb-sm bg-red-50 text-red-700 hover:bg-red-100 transition-colors text-t-sm font-semibold"
            >
              <LogOut size={14} strokeWidth={1.75} />
              Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default TopNav;
